import { useEffect, useState } from 'react'
import { drainQueue, readQueue, type QueuedMutation } from '../portal/execution-state'
import { readSession } from '../../lib/api/session-storage'

/**
 * `navigator.onLine` MENTE com frequência: reporta conectado num Wi-Fi sem
 * saída — exatamente o cenário de academia. Então o estado só vira offline
 * quando uma requisição falha de fato por rede, e volta quando uma tem
 * sucesso. Ver docs/specs/11-pwa.md.
 */
let networkFailed = false
const listeners = new Set<() => void>()

/** Cabeçalho que o service worker põe na resposta servida do cache por falta
 *  de rede. Ver apps/web/src/sw.ts. */
export const CACHE_FALLBACK_HEADER = 'x-steingym-cache'

/**
 * Traduz o RESULTADO de uma requisição em "está offline".
 *
 * Dois sinais, e nenhum deles é `fetch` rejeitar:
 *
 *  - 5xx: com um proxy, CDN ou load balancer na frente da API — o arranjo
 *    normal em produção — a API fora do ar chega como 502/503/504 e o
 *    `fetch` RESOLVE;
 *  - cabeçalho de cache: o service worker respondeu 200 do CacheStorage
 *    porque a rede não respondeu. Sem esse sinal a tela renderiza dados de
 *    ontem sem nenhum aviso, que é pior que um erro honesto.
 *
 * 4xx passa como online: é resposta legítima de um servidor que respondeu.
 */
export function isOfflineResponse(response: Pick<Response, 'status' | 'headers'>): boolean {
  if (response.headers.get(CACHE_FALLBACK_HEADER) !== null) return true
  return response.status >= 500
}

export function reportNetworkFailure(): void {
  if (networkFailed) return
  networkFailed = true
  for (const listener of listeners) listener()
}

export function reportNetworkSuccess(): void {
  if (!networkFailed) return
  networkFailed = false
  for (const listener of listeners) listener()
}

export function useOnline(): boolean {
  const [online, setOnline] = useState(
    () => (typeof navigator === 'undefined' || navigator.onLine) && !networkFailed,
  )

  useEffect(() => {
    const sync = (): void => setOnline(navigator.onLine && !networkFailed)

    listeners.add(sync)
    window.addEventListener('online', sync)
    window.addEventListener('offline', sync)

    return () => {
      listeners.delete(sync)
      window.removeEventListener('online', sync)
      window.removeEventListener('offline', sync)
    }
  }, [])

  return online
}

/**
 * Drena a fila de conclusões feitas offline na reconexão e na abertura.
 * Ver docs/specs/11-pwa.md §11.
 */
export function useOfflineQueueDrain(slug: string | undefined): { pending: number } {
  const [pending, setPending] = useState(() => readQueue().length)

  useEffect(() => {
    let cancelled = false

    async function drain(): Promise<void> {
      if (readQueue().length === 0) return

      const session = readSession(slug)
      if (!session) return

      await drainQueue(async (mutation: QueuedMutation) => {
        const response = await fetch(mutation.endpoint, {
          method: mutation.method,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.accessToken}`,
          },
          body: JSON.stringify(mutation.body),
        })

        return { ok: response.ok, status: response.status }
      })

      if (!cancelled) setPending(readQueue().length)
    }

    void drain()

    const onOnline = (): void => void drain()
    window.addEventListener('online', onOnline)

    return () => {
      cancelled = true
      window.removeEventListener('online', onOnline)
    }
  }, [slug])

  return { pending }
}
